import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.#initDropdowns();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#cleanupDropdowns();
  }

  /** @type {((e: MouseEvent) => void) | undefined} */
  #outsideClickHandler;

  /**
   * Initializes custom dropdown triggers and keyboard navigation.
   */
  #initDropdowns() {
    const triggers = this.querySelectorAll('[data-dropdown-trigger]');
    if (!triggers.length) return;

    for (const trigger of triggers) {
      trigger.addEventListener('click', this.#handleTriggerClick);
      trigger.addEventListener('keydown', this.#handleTriggerKeydown);
    }

    for (const panel of this.querySelectorAll('[data-dropdown-panel]')) {
      panel.addEventListener('keydown', this.#handlePanelKeydown);
    }
  }

  /**
   * Cleans up dropdown event listeners.
   */
  #cleanupDropdowns() {
    if (this.#outsideClickHandler) {
      document.removeEventListener('click', this.#outsideClickHandler, true);
      this.#outsideClickHandler = undefined;
    }
  }

  /**
   * Handles trigger button click.
   * @param {Event} e
   */
  #handleTriggerClick = (e) => {
    const trigger = /** @type {HTMLElement} */ (e.currentTarget);
    const expanded = trigger.getAttribute('aria-expanded') === 'true';

    // Close all other open panels first
    for (const other of this.querySelectorAll('[data-dropdown-trigger][aria-expanded="true"]')) {
      if (other !== trigger) this.#closeDropdown(/** @type {HTMLElement} */ (other));
    }

    if (expanded) {
      this.#closeDropdown(trigger);
    } else {
      this.#openDropdown(trigger);
    }
  };

  /**
   * Opens a dropdown panel.
   * @param {HTMLElement} trigger
   */
  #openDropdown(trigger) {
    const panel = trigger.nextElementSibling;
    if (!panel || !panel.hasAttribute('data-dropdown-panel')) return;

    trigger.setAttribute('aria-expanded', 'true');
    panel.removeAttribute('hidden');

    // Focus the checked item or first item
    const checked = panel.querySelector('input:checked');
    if (checked) {
      /** @type {HTMLElement | null} */ (checked.closest('label'))?.focus();
    }

    // Close on outside click
    this.#outsideClickHandler = (e) => {
      const target = /** @type {Node} */ (e.target);
      if (!trigger.contains(target) && !panel.contains(target)) {
        this.#closeDropdown(trigger);
      }
    };
    // Use setTimeout so the current click event doesn't immediately trigger the handler
    setTimeout(() => {
      if (this.#outsideClickHandler) {
        document.addEventListener('click', this.#outsideClickHandler, true);
      }
    }, 0);
  }

  /**
   * Closes a dropdown panel.
   * @param {HTMLElement} trigger
   */
  #closeDropdown(trigger) {
    const panel = trigger.nextElementSibling;
    if (panel && panel.hasAttribute('data-dropdown-panel')) {
      panel.setAttribute('hidden', '');
    }
    trigger.setAttribute('aria-expanded', 'false');

    if (this.#outsideClickHandler) {
      document.removeEventListener('click', this.#outsideClickHandler, true);
      this.#outsideClickHandler = undefined;
    }
  }

  /**
   * Closes the dropdown panel containing the target element (used after selection).
   * @param {EventTarget} target
   */
  #closeDropdownForTarget(target) {
    if (!(target instanceof HTMLElement)) return;
    const panel = target.closest('[data-dropdown-panel]');
    if (!panel) return;

    const trigger = panel.previousElementSibling;
    if (trigger && trigger.hasAttribute('data-dropdown-trigger')) {
      this.#closeDropdown(/** @type {HTMLElement} */ (trigger));
      /** @type {HTMLElement} */ (trigger).focus();
    }
  }

  /**
   * Keyboard handler for the trigger button.
   * @param {Event} e
   */
  #handleTriggerKeydown = (e) => {
    const event = /** @type {KeyboardEvent} */ (e);
    const trigger = /** @type {HTMLElement} */ (e.currentTarget);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (trigger.getAttribute('aria-expanded') !== 'true') {
        this.#openDropdown(trigger);
      }
      const panel = trigger.nextElementSibling;
      if (!panel) return;
      const items = /** @type {HTMLElement[]} */ (Array.from(panel.querySelectorAll('.variant-option__image-card')));
      if (items.length) {
        const focusIndex = event.key === 'ArrowDown' ? 0 : items.length - 1;
        items[focusIndex]?.focus();
      }
    } else if (event.key === 'Escape') {
      this.#closeDropdown(trigger);
      trigger.focus();
    }
  };

  /**
   * Keyboard handler for items inside the dropdown panel.
   * @param {Event} e
   */
  #handlePanelKeydown = (e) => {
    const event = /** @type {KeyboardEvent} */ (e);
    const panel = /** @type {HTMLElement} */ (e.currentTarget);
    const items = /** @type {HTMLElement[]} */ (Array.from(panel.querySelectorAll('.variant-option__image-card')));
    const currentIndex = items.indexOf(/** @type {HTMLElement} */ (document.activeElement?.closest('.variant-option__image-card')));

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = currentIndex + 1 < items.length ? currentIndex + 1 : 0;
      items[next]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = currentIndex - 1 >= 0 ? currentIndex - 1 : items.length - 1;
      items[prev]?.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const label = /** @type {HTMLElement | null} */ (document.activeElement?.closest('.variant-option__image-card'));
      const input = label?.querySelector('input');
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (event.key === 'Escape') {
      const trigger = panel.previousElementSibling;
      if (trigger && trigger.hasAttribute('data-dropdown-trigger')) {
        this.#closeDropdown(/** @type {HTMLElement} */ (trigger));
        /** @type {HTMLElement} */ (trigger).focus();
      }
    } else if (event.key === 'Tab') {
      const trigger = panel.previousElementSibling;
      if (trigger && trigger.hasAttribute('data-dropdown-trigger')) {
        this.#closeDropdown(/** @type {HTMLElement} */ (trigger));
      }
    }
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.#closeDropdownForTarget(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.allSelectedOptionsValues.length && !source) {
      params.push(`option_values=${this.allSelectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    // Match by block-id first so multiple variant-picker blocks on the same product
    // each morph with their own counterpart in the response (not always the first one).
    const tag = this.tagName.toLowerCase();
    const blockId = this.dataset.blockId;
    const newVariantPickerSource = blockId
      ? newHtml.querySelector(`${tag}[data-block-id="${blockId}"]`) ?? newHtml.querySelector(tag)
      : newHtml.querySelector(tag);

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    // Morph sibling pickers for the same product so their prices/badges update
    const productId = this.dataset.productId;
    if (productId) {
      const tag = this.tagName.toLowerCase();
      /** @type {NodeListOf<VariantPicker>} */
      const siblings = document.querySelectorAll(`${tag}[data-product-id="${productId}"]`);
      for (const sibling of siblings) {
        if (sibling === this) continue;
        const siblingBlockId = sibling.dataset.blockId;
        const siblingSource = siblingBlockId
          ? newHtml.querySelector(`${tag}[data-block-id="${siblingBlockId}"]`)
          : null;
        if (siblingSource) {
          morph(sibling, siblingSource, {
            ...MORPH_OPTIONS,
            getNodeKey: (node) => {
              if (!(node instanceof HTMLElement)) return undefined;
              return node.dataset.key;
            },
          });
          sibling.updateVariantPickerCss();
        }
      }
    }

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Validates that every option fieldset has a checked selection.
   * If any fieldset has no selection, flashes a visual indicator on it,
   * scrolls it into view, and returns false.
   * @returns {boolean} True if all options are selected, false otherwise.
   */
  validateSelection() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    // Dropdown selects always have a value, so only radio/button fieldsets need validation.

    for (const fieldset of fieldsets) {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      const hasSelection = radios.some((radio) => radio.checked);

      if (!hasSelection) {
        // Flash the fieldset
        fieldset.classList.remove('variant-option--validation-flash');
        // Force reflow so re-adding the class restarts the animation
        void fieldset.offsetWidth;
        fieldset.classList.add('variant-option--validation-flash');

        // Scroll into view
        fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldset.focus({ preventScroll: true });

        // Remove the class after animation completes
        fieldset.addEventListener(
          'animationend',
          () => {
            fieldset.classList.remove('variant-option--validation-flash');
          },
          { once: true }
        );

        return false;
      }
    }

    return true;
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values from this picker only.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }

  /**
   * Gets selected option values from all variant-picker instances for the same product on the page.
   * This ensures that when one split picker (e.g. Color) updates, the selections from sibling
   * pickers (e.g. Height) are included in the request so Shopify resolves the correct variant.
   * @returns {string[]} Merged option value IDs, deduplicated by option position.
   */
  get allSelectedOptionsValues() {
    const productId = this.dataset.productId;
    if (!productId) return this.selectedOptionsValues;

    // Collect all pickers for the same product
    /** @type {VariantPicker[]} */
    const siblings = Array.from(document.querySelectorAll(`variant-picker[data-product-id="${productId}"]`));
    if (siblings.length <= 1) return this.selectedOptionsValues;

    // Merge: values from this picker take priority (they reflect the just-changed option),
    // then fill in any option positions not covered by this picker from siblings.
    const mine = this.selectedOptionsValues;
    // Build a set of option positions already covered by this picker
    const myPositions = new Set(
      Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'))
        .map((el) => /** @type {HTMLElement} */ (el).dataset.inputId?.split('-')[0])
        .filter(Boolean)
    );

    const merged = [...mine];
    for (const sibling of siblings) {
      if (sibling === this) continue;
      const siblingInputs = Array.from(sibling.querySelectorAll('select option[selected], fieldset input:checked'));
      for (const input of siblingInputs) {
        const el = /** @type {HTMLElement} */ (input);
        const pos = el.dataset.inputId?.split('-')[0];
        if (pos && !myPositions.has(pos) && el.dataset.optionValueId) {
          merged.push(el.dataset.optionValueId);
          myPositions.add(pos);
        }
      }
    }

    return merged;
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
